import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import { axiosSalary } from "../api/axiosInstance";

const AddEditShiftMaster = ({ editingData, onClose, onSuccess }) => {
    let storedUser = {};
    try {
        storedUser = JSON.parse(localStorage.getItem("user") || "{}");
    } catch { }
    const groupId = storedUser?.groupId || 0;
    const userId = storedUser?.userId || 0;

    const [menuOpen, setMenuOpen] = useState(true);

    /* ================= SHIFT OPTIONS ================= */
    const shiftOptions = [
        { label: "Open", value: 1 },
        { label: "Reporting", value: 2 },
        { label: "Closing", value: 3 },
        { label: "Break Shift", value: 4 },

    ];

    // Shift type that allows a custom, editable shift name
    const CUSTOM_NAME_SHIFT_TYPE = 4;

    /* ================= EMPTY SESSION TEMPLATE ================= */
    const emptySession = () => ({
        sequenceNo: 0,
        inTime: "",
        outTime: "",
        outTimeNextDay: false,
        punchOutGraceMinutes: 0,
    });

    /* ================= STATE ================= */
    const [form, setForm] = useState({
        shiftName: "Open",
        shiftType: 1,
        reportingTime: null,
        closingTime: null,
        lateGraceMinutes: 0,
        earlyLeaveGraceMinutes: 0,
    });

    const [breakShiftSessions, setBreakShiftSessions] = useState([emptySession()]);
    const [selectedShift, setSelectedShift] = useState(shiftOptions[0]);
    const [saving, setSaving] = useState(false);

    /* ================= REFS ================= */
    const shiftSelectRef = useRef(null);
    const shiftNameRef = useRef(null);
    const reportingRef = useRef(null);
    const closingRef = useRef(null);
    const lateGraceRef = useRef(null);
    const earlyGraceRef = useRef(null);
    const saveRef = useRef(null);

    /* ================= LOAD EDIT DATA ================= */
    useEffect(() => {
        if (editingData) {
            // Coerce shiftType to a number in case the API returns it as a
            // string (e.g. "4") — otherwise strict === checks against
            // CUSTOM_NAME_SHIFT_TYPE silently fail.
            const editingShiftType = Number(editingData.shiftType);

            const selected = shiftOptions.find(
                (s) => s.value === editingShiftType
            ) || shiftOptions[0];

            setForm({
                // For Break Shift, always prefer the saved custom name.
                // Only fall back to the generic option label when the API
                // genuinely didn't return a name.
                shiftName:
                    editingData.shiftName && editingData.shiftName.trim() !== ""
                        ? editingData.shiftName
                        : selected.label,
                shiftType: editingShiftType || selected.value,
                reportingTime: editingData.reportingTime || null,
                closingTime: editingData.closingTime || null,
                lateGraceMinutes: editingData.lateGraceMinutes || 0,
                earlyLeaveGraceMinutes: editingData.earlyLeaveGraceMinutes || 0,
            });
            setSelectedShift(selected);

            if (Array.isArray(editingData.breakShiftSessions) && editingData.breakShiftSessions.length > 0) {
                setBreakShiftSessions(
                    editingData.breakShiftSessions.map((s, idx) => ({
                        sequenceNo: s.sequenceNo || idx + 1,
                        inTime: s.inTime || "",
                        outTime: s.outTime || "",
                        outTimeNextDay: !!s.outTimeNextDay,
                        punchOutGraceMinutes: s.punchOutGraceMinutes || 0,
                    }))
                );
            } else {
                setBreakShiftSessions([emptySession()]);
            }
        }

        // focus shift select on open
        setTimeout(() => shiftSelectRef.current?.focus(), 100);
    }, [editingData]);

    /* ================= HANDLE CHANGE ================= */
    const handleChange = (field, value) =>
        setForm((prev) => ({ ...prev, [field]: value }));

    /* ================= HANDLE SHIFT SELECT ================= */
    const handleShiftSelect = (option) => {
        setSelectedShift(option);
        handleChange("shiftName", option.label);
        handleChange("shiftType", option.value);

        // If the user picked "Break Shift", send focus to the editable
        // name field instead of Reporting Time, since they'll likely
        // want to rename it right away.
        if (option.value === CUSTOM_NAME_SHIFT_TYPE) {
            setTimeout(() => shiftNameRef.current?.focus(), 50);
        } else {
            setTimeout(() => reportingRef.current?.focus(), 50);
        }
    };

    /* ================= KEYBOARD NAVIGATION ================= */
    const handleKey = (e, nextRef, prevRef) => {
        if (e.key === "Enter") {
            e.preventDefault();
            nextRef?.current?.focus();
        }
        if (e.key === "Escape") {
            e.preventDefault();
            prevRef?.current ? prevRef.current.focus() : onClose?.();
        }
    };

    /* ================= BREAK SHIFT SESSION HANDLERS ================= */
    const handleSessionChange = (index, field, value) => {
        setBreakShiftSessions((prev) => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const addSession = () => {
        setBreakShiftSessions((prev) => [...prev, emptySession()]);
    };

    const removeSession = (index) => {
        setBreakShiftSessions((prev) => {
            if (prev.length === 1) return prev; // keep at least one row
            return prev.filter((_, i) => i !== index);
        });
    };

    /* ================= SAVE ================= */
    const handleSave = async () => {
        if (!form.shiftName) return alert("Select Shift Name");

        try {
            setSaving(true);

            // Ensure HH:mm:ss format
            const formatTime = (time) => {
                if (!time || time.trim() === "") return null;
                return time.length === 5 ? `${time}:00` : time;
            };

            const reportingTime = formatTime(form.reportingTime);
            const closingTime = formatTime(form.closingTime);

            // Build sessions payload with sequence numbers auto-assigned
            const sessionsPayload = breakShiftSessions
                .filter((s) => s.inTime || s.outTime) // skip fully empty rows
                .map((s, idx) => ({
                    sequenceNo: idx + 1,
                    inTime: formatTime(s.inTime),
                    outTime: formatTime(s.outTime),
                    outTimeNextDay: !!s.outTimeNextDay,
                    punchOutGraceMinutes: Number(s.punchOutGraceMinutes) || 0,
                }));

            const payload = {
                groupId,
                shiftId: editingData?.shiftId || 0,
                shiftName: form.shiftName,
                shiftType: form.shiftType,
                reportingTime,
                closingTime,
                lateGraceMinutes: Number(form.lateGraceMinutes),
                earlyLeaveGraceMinutes: Number(form.earlyLeaveGraceMinutes),
                breakShiftSessions: sessionsPayload,
                userId,
            };

            await axiosSalary.post("/AttendanceShift/UpsertShift", payload);
            onSuccess?.();
            onClose?.();
        } catch (err) {
            console.error("Save shift error:", err);
            alert("Failed to save shift");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-3">
            <div className="bg-white w-full max-w-lg rounded-xl shadow-xl max-h-[90vh] overflow-auto">
                {/* HEADER */}
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="font-bold text-lg">
                        {editingData ? "Edit Shift" : "Add Shift"}
                    </h2>
                    <button onClick={onClose} className="text-red-500 text-xl font-bold">
                        ✕
                    </button>
                </div>

                {/* BODY */}
                <div className="p-3 grid grid-cols-1 gap-3">
                    {/* SHIFT SELECT */}
                    <div>
                        <label className="text-sm font-medium">Shift Name *</label>
                        <Select
                            ref={shiftSelectRef}
                            options={shiftOptions}
                            value={selectedShift}
                            onChange={(option) => {
                                handleShiftSelect(option);
                                setMenuOpen(false);
                            }}
                            menuIsOpen={menuOpen}
                            onMenuOpen={() => setMenuOpen(true)}
                            onMenuClose={() => setMenuOpen(false)}
                            onFocus={() => setMenuOpen(true)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    const highlightedOption = shiftSelectRef.current?.state?.focusedOption;
                                    if (highlightedOption) {
                                        handleShiftSelect(highlightedOption);
                                        setMenuOpen(false);
                                    }
                                }
                                if (e.key === "Escape") {
                                    e.preventDefault();
                                    setMenuOpen(false);
                                    onClose?.();
                                }
                            }}
                            placeholder="Select Shift"
                        />
                    </div>

                    {/* CUSTOM SHIFT NAME - only for Break Shift */}
                    {form.shiftType === CUSTOM_NAME_SHIFT_TYPE && (
                        <div>
                            <label className="text-sm font-medium">Custom Shift Name</label>
                            <input
                                ref={shiftNameRef}
                                type="text"
                                value={form.shiftName}
                                onChange={(e) => handleChange("shiftName", e.target.value)}
                                onKeyDown={(e) => handleKey(e, reportingRef, shiftSelectRef)}
                                placeholder="Break Shift"
                                className="w-full border rounded-lg px-2 py-1 mt-1"
                            />
                        </div>
                    )}

                    {/* REPORTING TIME */}
                    <div>
                        <label className="text-sm font-medium">Reporting Time *</label>
                        <input
                            ref={reportingRef}
                            type="time"
                            value={form.reportingTime || ""}
                            onChange={(e) => handleChange("reportingTime", e.target.value)}
                            onKeyDown={(e) => handleKey(e, closingRef, shiftSelectRef)}
                            className="w-full border rounded-lg px-2 py-1 mt-1"
                        />
                    </div>

                    {/* CLOSING TIME */}
                    <div>
                        <label className="text-sm font-medium">Closing Time *</label>
                        <input
                            ref={closingRef}
                            type="time"
                            value={form.closingTime || ""}
                            onChange={(e) => handleChange("closingTime", e.target.value)}
                            onKeyDown={(e) => handleKey(e, lateGraceRef, reportingRef)}
                            className="w-full border rounded-lg px-2 py-1 mt-1"
                        />
                    </div>

                    {/* LATE GRACE */}
                    <div>
                        <label className="text-sm font-medium">Late Grace Minutes</label>
                        <input
                            ref={lateGraceRef}
                            type="number"
                            value={form.lateGraceMinutes}
                            onChange={(e) => handleChange("lateGraceMinutes", e.target.value)}
                            onKeyDown={(e) => handleKey(e, earlyGraceRef, closingRef)}
                            className="w-full border rounded-lg px-2 py-1 mt-1"
                        />
                    </div>

                    {/* EARLY LEAVE GRACE */}
                    <div>
                        <label className="text-sm font-medium">Early Leave Grace Minutes</label>
                        <input
                            ref={earlyGraceRef}
                            type="number"
                            value={form.earlyLeaveGraceMinutes}
                            onChange={(e) =>
                                handleChange("earlyLeaveGraceMinutes", e.target.value)
                            }
                            onKeyDown={(e) => handleKey(e, null, lateGraceRef)}
                            className="w-full border rounded-lg px-2 py-1 mt-1"
                        />
                    </div>

                    {/* BREAK SHIFT SESSIONS */}
                    <div className="border-t pt-3 mt-1">
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm font-semibold">Break Shift Sessions</label>
                            <button
                                type="button"
                                onClick={addSession}
                                className="text-xs px-2 py-1 rounded-md bg-purple-100 text-purple-700 hover:bg-purple-200"
                            >
                                + Add Session
                            </button>
                        </div>

                        <div className="flex flex-col gap-3">
                            {breakShiftSessions.map((session, index) => (
                                <div
                                    key={index}
                                    className="border rounded-lg p-2 relative bg-gray-50"
                                >
                                    <div className="flex justify-between items-center mb-2">
                                        <span className="text-xs font-medium text-gray-500">
                                            Session #{index + 1}
                                        </span>
                                        {breakShiftSessions.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeSession(index)}
                                                className="text-red-500 text-xs font-bold hover:text-red-700"
                                            >
                                                Remove
                                            </button>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-2">
                                        {/* IN TIME */}
                                        <div>
                                            <label className="text-xs font-medium">In Time</label>
                                            <input
                                                type="time"
                                                value={session.inTime || ""}
                                                onChange={(e) =>
                                                    handleSessionChange(index, "inTime", e.target.value)
                                                }
                                                className="w-full border rounded-lg px-2 py-1 mt-1 text-sm"
                                            />
                                        </div>

                                        {/* OUT TIME */}
                                        <div>
                                            <label className="text-xs font-medium">Out Time</label>
                                            <input
                                                type="time"
                                                value={session.outTime || ""}
                                                onChange={(e) =>
                                                    handleSessionChange(index, "outTime", e.target.value)
                                                }
                                                className="w-full border rounded-lg px-2 py-1 mt-1 text-sm"
                                            />
                                        </div>

                                        {/* PUNCH OUT GRACE MINUTES */}
                                        <div>
                                            <label className="text-xs font-medium">
                                                Punch Out Grace (min)
                                            </label>
                                            <input
                                                type="number"
                                                value={session.punchOutGraceMinutes}
                                                onChange={(e) =>
                                                    handleSessionChange(
                                                        index,
                                                        "punchOutGraceMinutes",
                                                        e.target.value
                                                    )
                                                }
                                                className="w-full border rounded-lg px-2 py-1 mt-1 text-sm"
                                            />
                                        </div>

                                        {/* OUT TIME NEXT DAY */}
                                        <div className="flex items-center gap-2 mt-5">
                                            <input
                                                type="checkbox"
                                                id={`nextDay-${index}`}
                                                checked={session.outTimeNextDay}
                                                onChange={(e) =>
                                                    handleSessionChange(
                                                        index,
                                                        "outTimeNextDay",
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                            <label htmlFor={`nextDay-${index}`} className="text-xs">
                                                Out Time Next Day
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* FOOTER */}
                <div className="flex justify-end gap-2 p-4 border-t">
                    <button onClick={onClose} className="px-4 py-2 rounded-lg border">
                        Cancel
                    </button>
                    <button
                        ref={saveRef}
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
                    >
                        {saving ? "Saving..." : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddEditShiftMaster;